import { useEffect, useEffectEvent } from "react";
import { useNavigate } from "react-router";
import { Button, Icon } from "@sellar-io/components";
import { CheckoutPageLayout } from "../stockList/checkoutV2/lib/CheckoutPageLayout";
import { CheckoutPageLoading } from "../stockList/checkoutV2/lib/CheckoutPageLoading";
import {
  DISCOVER_DISTRIBUTION,
  ORDERS,
  UNIFIED_CHECKOUT_BASKET_OVERVIEW,
} from "../../constants/routes";
import { useUnifiedCheckoutSuccessPageData } from "./hooks/useUnifiedCheckoutSuccessPageData";
import { UnifiedCheckoutOrderDisplay } from "./components/UnifiedCheckoutOrderDisplay";
import { useUnifiedCheckoutTracking } from "./useUnifiedCheckoutTracking";
import { useUnifiedCheckoutSuccessDetails } from "./hooks/useUnifiedCheckoutSuccessDetails";
import { UnifiedCheckoutRemainingBasketAlert } from "./components/UnifiedCheckoutRemainingBasketAlert";
import { UnifiedCheckoutDeliveryDetails } from "./components/UnifiedCheckoutDeliveryDetails";
import { UnifiedCheckoutNextSteps } from "./components/UnifiedCheckoutNextSteps";

export const UnifiedCheckoutSuccessPage = () => {
  const navigate = useNavigate();
  const { loading, deliveryDates, orders } =
    useUnifiedCheckoutSuccessPageData();
  const successDetails = useUnifiedCheckoutSuccessDetails({
    deliveryDates,
    orders,
  });
  const { trackStepViewed } = useUnifiedCheckoutTracking();
  const trackSuccessViewed = useEffectEvent(() => trackStepViewed("success"));
  useEffect(() => {
    if (!loading) trackSuccessViewed();
  }, [loading]);

  if (loading) {
    return <CheckoutPageLoading title="Checkout" description="" />;
  }

  if (orders.length === 0) {
    return (
      <div className="flex h-screen min-h-0 flex-col bg-zinc-50">
        <CheckoutPageLayout
          title="Checkout"
          alerts={null}
          aside={null}
          layout="centered"
          onClickLeftIcon={() => navigate(DISCOVER_DISTRIBUTION)}
        >
          <div className="flex flex-col items-center justify-center gap-6 py-12">
            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
              <Icon icon="CHECK" size={1.5} />
            </div>
            <div className="flex flex-col items-center gap-2 text-center">
              <h1 className="text-2xl font-bold text-zinc-900">
                Payment successful
              </h1>
              <p className="max-w-md text-zinc-600">
                Your orders have been placed and payment has been received.
              </p>
            </div>
            <Button onClick={() => navigate(DISCOVER_DISTRIBUTION)}>
              Continue shopping
            </Button>
            <Button color="transparent" onClick={() => navigate(ORDERS)}>
              View all orders
              <Icon icon="CHEVRON_RIGHT" />
            </Button>
          </div>
        </CheckoutPageLayout>
      </div>
    );
  }

  const remainingBasketUuid = successDetails.remainingBasketUuid;

  return (
    <div className="flex h-screen min-h-0 flex-col bg-zinc-50">
      <CheckoutPageLayout
        title="Checkout"
        alerts={null}
        aside={null}
        layout="single"
        onClickLeftIcon={() => navigate(DISCOVER_DISTRIBUTION)}
      >
        <div className="flex flex-col gap-3 py-4 pb-10">
          <UnifiedCheckoutOrderDisplay orders={orders}>
            {remainingBasketUuid && (
              <UnifiedCheckoutRemainingBasketAlert
                count={successDetails.remainingBasketCount}
                itemCount={successDetails.remainingBasketItemCount}
                onClick={() =>
                  navigate(
                    UNIFIED_CHECKOUT_BASKET_OVERVIEW.href({
                      sdBasketUuid: remainingBasketUuid,
                    }),
                  )
                }
              />
            )}

            <div className="mt-2 grid grid-cols-2 gap-2">
              <Button
                className="w-full bg-zinc-900 text-white"
                onClick={() => navigate(DISCOVER_DISTRIBUTION)}
              >
                Back to Market
              </Button>
              <Button
                color="secondary"
                className="w-full"
                onClick={() => navigate(ORDERS)}
              >
                View all orders
                <Icon icon="CHEVRON_RIGHT" size={0.7} />
              </Button>
            </div>
          </UnifiedCheckoutOrderDisplay>

          <UnifiedCheckoutDeliveryDetails
            contactName={successDetails.contactName}
            contactPhone={successDetails.contactPhone}
            deliveryGroups={successDetails.deliveryGroups}
            deliveryPreference={successDetails.deliveryPreference}
            timePreference={successDetails.timePreference}
          />
          <UnifiedCheckoutNextSteps
            deliveryDates={deliveryDates}
            deliveryGroups={successDetails.deliveryGroups}
            deliveryPreference={successDetails.deliveryPreference}
          />
        </div>
      </CheckoutPageLayout>
    </div>
  );
};
